import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { removeIndicator, removeMapIndicator, setWorkspaceLoaded, startNewWorkspace } from "store/workspaceSlice";
import { MdOutlineDeleteOutline } from "react-icons/md";
import SaveWorkspaceModal from "components/authentication/signup-login/SaveWorkspaceModal";
import { useAuth } from 'components/authentication/signup-login/AuthContext';
import AuthModalHandler from 'components/authentication/signup-login/AuthModalHandler';

const WorkspaceSection = ({ indicators, locations }) => {
    const dispatch = useDispatch();
    const { isAuthenticated, login } = useAuth();

    // Modal states
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [modalMode, setModalMode] = useState('save');
    const [pendingSaveMode, setPendingSaveMode] = useState(null);

    // Redux selectors for workspace metadata
    const isWorkspaceLoaded = useSelector(state => state.workspace.isWorkspaceLoaded);
    const loadedWorkspaceName = useSelector(state => state.workspace.loadedWorkspaceName);
    const hasUnsavedChanges = useSelector(state => state.workspace.hasUnsavedChanges);
    const currentWorkspace = useSelector(state => state.workspace.currentWorkspace);

    // Handle location removal
    const handleRemoveLocation = (locationId) => {
        dispatch(removeMapIndicator(locationId));
    };

    // Handle indicator removal
    const handleRemoveIndicator = (indicatorCode) => {
        dispatch(removeIndicator(indicatorCode));
    };

    // Handle save click
    const handleSaveClick = (mode = 'save') => {
        if (!isAuthenticated) {
            // Store the save mode and open login modal
            setPendingSaveMode(mode);
            setShowAuthModal(true);
            return;
        }

        if (indicators.length === 0) {
            return;
        }

        setModalMode(mode);
        setShowSaveModal(true);
    };

    // Handle login success
    const handleLoginSuccess = (response) => {
        // Extract user data and token from response
        const loginData = response.data || response;
        let userData = null;
        let token = null;

        if (loginData && loginData.user) {
            userData = loginData.user;
            token = loginData.token;
        } else {
            // Handle different response structures
            userData = {
                name: response.name || response.user?.name || 'User',
                email: response.email || response.user?.email || 'user@example.com'
            };
            token = response.token || response.access_token;
        }

        // Update global auth context
        login(userData, token);

        // Close auth modal
        setShowAuthModal(false);

        // Execute pending save action with proper authentication check
        if (pendingSaveMode) {
            setTimeout(() => {
                // Double-check authentication before opening save modal
                const token = localStorage.getItem('authToken');
                const userData = localStorage.getItem('userData');

                if (token && userData) {
                    setModalMode(pendingSaveMode);
                    setShowSaveModal(true);
                }
                setPendingSaveMode(null);
            }, 1000); // Increased delay to ensure auth state is fully updated
        }
    };

    // Handle save success
    const handleSaveSuccess = (workspaceData) => {
        // Always load the saved workspace as current (for both new saves and save as)
        if (workspaceData.id && workspaceData.name) {
            // Add a small delay to ensure the workspace is properly created
            setTimeout(() => {
                // Dispatch action to load the workspace
                dispatch(setWorkspaceLoaded({
                    id: workspaceData.id,
                    name: workspaceData.name,
                    workspace: workspaceData
                }));
            }, 100);
        }

        // Close the modal
        setShowSaveModal(false);
    };

    // Handle new workspace
    const handleNewWorkspace = () => {
        // Clear all indicators and locations and reset to "My Workspace" state
        dispatch(startNewWorkspace());
    };

    // Helper function for geo level labels
    const geoLevelLabel = (level) => {
        switch (level) {
            case 1: return "National";
            case 2: return "State / UT";
            case 3: return "District";
            case 4: return "ULG";
            default: return "Unknown";
        }
    };

    return (
        <>
            {/* Workspace Header */}
            <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                <h5 className="mb-0 text-dark">
                    <i className="bi bi-person-workspace me-2"></i>
                    {isWorkspaceLoaded ? loadedWorkspaceName : 'My Workspace'}
                    {hasUnsavedChanges && <span className="text-warning ms-2">*</span>}
                </h5>
                {/* <div className="d-flex gap-2">
                    <button 
                        className="btn btn-sm btn-primary"
                        onClick={() => handleSaveClick(isWorkspaceLoaded ? 'update' : 'save')} 
                    >
                        <i className={`bi ${isWorkspaceLoaded ? 'bi-arrow-repeat' : 'bi-save'} me-1`}></i> 
                        {isWorkspaceLoaded ? 'Update' : 'Save'}
                    </button>

                    <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => handleSaveClick('saveAs')}
                    >
                        <i className="bi bi-folder-plus me-1"></i> Save As
                    </Button>
                    <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={handleNewWorkspace}
                    >
                        <i className="bi bi-plus-circle me-1"></i> New
                    </Button>
                </div> */}
            </div>

            {/* Indicators and Locations Lists */}
            <div className="row">
                <div className="col-md-6 mt-3">
                    <h5 className='f-14 fw-bold mb-3'> Indicators</h5>
                    {indicators.length === 0 ? (
                        <p className="text-muted">No indicators selected</p>
                    ) : (
                        <ul className="list-group mb-3">
                            {indicators.map((ind) => (
                                <li key={ind.indicator_code} className="list-group-item">
                                    <div>
                                        <div className="fw-bold text-primary" style={{ fontSize: "13px" }}>
                                            {ind.indicator_name && (
                                                <>
                                                    <span>{ind.indicator_name}</span>
                                                    {ind.dataset_name && <span> ({ind.dataset_name})</span>}
                                                </>
                                            )}

                                            {ind.indicator_date && ind.indicator_date !== "NA" && (
                                                <>
                                                    {" | "}
                                                    <span
                                                        style={{
                                                            backgroundColor: "#2c7ce5",
                                                            color: "#fff",
                                                            padding: "0 6px",
                                                            borderRadius: "4px",
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        {ind.indicator_date}
                                                    </span>
                                                </>
                                            )}

                                            {ind.agency_name && ind.agency_name !== "NA" && (
                                                <>
                                                    {" | "}
                                                    <span>{ind.agency_name}</span>
                                                </>
                                            )}

                                            {/* {Array.isArray(ind.geo_levels) && ind.geo_levels.length > 0 && (
                                                <>
                                                    {" | "}
                                                    <span>{ind.geo_levels.join(", ")}</span>
                                                </>
                                            )} */}
                                        </div>
                                        <div className="text small">
                                              {/* Available Geo Levels Section */}
  {ind.geo_levels && ind.geo_levels.length > 0 && (
    <div className="mt-1 mb-1">
      <span className="me-2" style={{ fontSize: "0.875rem" }}>Available Geo Levels:</span>
      {ind.geo_levels.map((level, idx) => (
        <span
          key={idx}
          style={{
            backgroundColor: "#28a745",
            color: "#fff",
            borderRadius: "0.25rem",
            padding: "0.2rem 0.5rem",
            marginRight: "0.3rem",
            fontSize: "0.75rem",
            display: "inline-block"
          }}
        >
          {level}
        </span>
      ))}
    </div>
  )}
                                            {[ind.sector_name, ind.sub_sector_name, ind.sub_sub_sector_name]
                                                .filter((val) => val && val !== "NA")
                                                .join(" | ")}
                                        </div>
                                    </div>
                                    <MdOutlineDeleteOutline
                                        className='text-danger p-a'
                                        onClick={() => handleRemoveIndicator(ind.indicator_code)}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="col-md-6 mt-3">
                    <h5 className='f-14 fw-bold mb-3'> Locations</h5>
                    <div className="list-group mb-3">
                        <div className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                                <span className="fw-bold">India</span>
                                <span className="small text-secondary ms-2">(National)</span>
                            </div>
                        </div>
                        {locations?.map((data, index) => {
                            const locationId = String(data.name || data.properties?.name || '');
                            const uniqueKey = `${locationId}-${index}`;
                            return (
                                <div key={uniqueKey} className="list-group-item d-flex justify-content-between align-items-center">
                                    <div>
                                        <span className="fw-bold">{data.name || data?.properties?.name}</span>
                                        <span className="small text-secondary ms-2">({geoLevelLabel(data?.geo_level_code || 4)})</span>
                                    </div>
                                    <MdOutlineDeleteOutline
                                        className='text-danger p-a'
                                        onClick={() => handleRemoveLocation(locationId)}
                                        title="Remove location"
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Save Workspace Modal */}
            <SaveWorkspaceModal
                show={showSaveModal}
                handleClose={() => setShowSaveModal(false)}
                onSave={handleSaveSuccess}
                selectedIndicators={indicators}
                selectedLocations={[]} // This will be handled by individual visualization components
                allMapFeatures={locations}
                mode={modalMode}
            />

            {/* Auth Modal for login when user tries to save without being logged in */}
            <AuthModalHandler
                show={showAuthModal}
                handleClose={() => {
                    setShowAuthModal(false);
                    setPendingSaveMode(null);
                }}
                initialMode="login"
                onLoginSuccess={handleLoginSuccess}
            />
        </>
    );
};

export default WorkspaceSection;
